<template>
  <div>
    <div class="row d-flex justify-content-center">
      <div class="col-12 col-md-10 col-lg-12 col-xl-10">
        <div
          v-if="subscriptionIsCancelled && !subscriptionIsExpired"
          class="row justify-content-center"
        >
          <div class="col-12 col-md-12 col-lg-8">
            <div class="alert alert-warning" role="alert">
              {{
                $t("message.cantChangeSubscriptionWarning").replace(
                  "$DATE",
                  dateToHuman(subscription.endDate)
                )
              }}
            </div>
          </div>
        </div>
        <div v-if="!saasShop" class="row justify-content-center">
          <div class="col-12 col-md-8">
            <div class="alert alert-info" role="alert">
              Boxx Finglas is not billed from here.
            </div>
          </div>
        </div>
        <div v-else class="row justify-content-center">
          <div
            v-for="plan in saasPlans"
            class="col-12 col-md-6 col-lg-6 col-xl-4"
            :key="plan.title"
          >
            <div :class="`card plan-card ${plan.title}`">
              <div class="plan-card__header">
                <h2 class="plan-card__header__title">
                  {{ plan.title }}
                  <span v-if="plan.beta" class="badge badge-warning">
                    {{ $t("message.beta") }}
                  </span>
                </h2>
                <span class="plan-card__header__price"
                  >€{{ plan.price }}/{{ $t("message.month") }}</span
                >
              </div>
              <div class="plan-card__body">
                <div
                  v-for="feature in plan.features"
                  :key="`${plan.name}__${feature.title}`"
                  :class="`plan-card__feature ${feature.class}`"
                >
                  <i
                    :class="
                      `now-ui-icons ${featureClassToIcon(
                        feature.class
                      )} plan-card__feature__icon`
                    "
                  ></i>
                  <div class="plan-card__feature__content">
                    <span class="plan-card__feature__content__title">
                      {{ feature.title }}
                      <span v-if="feature.limit">
                        &#8226; {{ feature.limit }}/{{ $t("message.month") }}
                      </span>
                    </span>
                    <span class="plan-card__feature__content__sub-title">
                      {{ feature.description }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="plan-card__footer">
                <button v-if="plan.comingSoon" class="btn btn-default" disabled>
                  {{ $t("message.comingSoon") }}
                </button>
                <button
                  v-if="
                    !plan.comingSoon &&
                      ((plan.id === subscription.plan &&
                        subscriptionIsExpired) ||
                        (plan.id !== subscription.plan &&
                          (subscriptionIsExpired || !subscriptionIsCancelled)))
                  "
                  class="btn btn-round btn-primary"
                  @click="onChoosePlan(plan)"
                >
                  {{ $t("message.go") }} {{ plan.title }}
                </button>
                <button
                  v-if="
                    plan.id === subscription.plan &&
                      subscriptionIsCancelled &&
                      !subscriptionIsExpired
                  "
                  class="btn btn-round btn-primary"
                  @click="confirmReactivateSubscription(plan)"
                >
                  {{ $t("message.reactivate") }}
                </button>
                <button
                  v-if="
                    plan.id === subscription.plan &&
                      !subscriptionIsCancelled &&
                      !subscriptionIsExpired
                  "
                  class="btn btn-round"
                  disabled
                >
                  {{ $t("message.currentPlan") }}
                </button>
                <button
                  v-if="
                    !plan.comingSoon &&
                      plan.id !== subscription.plan &&
                      subscriptionIsCancelled &&
                      !subscriptionIsExpired
                  "
                  class="btn btn-round"
                  disabled
                >
                  {{ $t("message.unavailable") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { dateToHuman } from "@/helpers/helpers";
import { SAAS_PLAN } from "@/helpers/saasBilling";

export default {
  data() {
    return {
      loader: null
    };
  },
  methods: {
    dateToHuman,
    ...mapActions([
      "fetchStripeSession",
      "startSaasCheckout",
      "changeSubscriptionPreview",
      "changeSubscription",
      "fetchPlans"
    ]),
    onChoosePlan(plan) {
      return this.subscriptionIsTrialPeriod || this.subscriptionIsExpired
        ? this.confirmNewSubscription(plan)
        : this.confirmChangeSubscription(plan);
    },
    checkoutUrls() {
      const origin =
        (typeof window !== "undefined" && window.location && window.location.origin) ||
        "https://app.smartbooks365.com";
      return {
        success_url: `${origin}/account?saas_checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/user/subscription?saas_checkout=cancel`
      };
    },
    async confirmNewSubscription(plan) {
      this.loader = this.$loading.show();

      try {
        if (this.saasShop && plan.id === "SAAS") {
          await this.startSaasCheckout(this.checkoutUrls());
          return;
        }
        await this.fetchStripeSession(plan.id);
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.changeSubscriptionFailure")
        });
      } finally {
        this.loader.hide();
        this.loader = null;
      }
    },
    async confirmChangeSubscription(plan) {
      this.loader = this.$loading.show();

      try {
        const { data } = await this.changeSubscriptionPreview(plan);

        this.loader.hide();

        const { cost, displayValue } = data;

        const changeSubscriptionConfirmationHTML = this.$t(
          "message.changeSubscriptionConfirmationHTML"
        )
          .replace("{{plan}}", plan.id)
          .replace("{{price}}", "€" + plan.price);

        const changeSubscriptionIncurredCostsMessageHTML =
          cost < 0
            ? this.$t(
                "message.changeSubscriptionCreditAddedToAccountHTML"
              ).replace("{{amount}}", displayValue)
            : this.$t(
                "message.changeSubscriptionCostAddedToNextBillHTML"
              ).replace("{{amount}}", displayValue);

        const result = await this.$swal({
          title: this.$t("message.areYouSure"),
          html: `${changeSubscriptionConfirmationHTML}${changeSubscriptionIncurredCostsMessageHTML}${this.$t(
            "message.changeSubscriptionNextBillMayChangeHTML"
          )}`,
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: this.$t("message.goBack"),
          confirmButtonText: this.$t("message.confirm")
        });

        if (!result.value) return;

        this.loader = this.$loading.show();

        await this.changeSubscription(plan);
        this.$router.push("/account/overview");
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.changeSubscriptionSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.changeSubscriptionFailure")
        });
      } finally {
        this.loader.hide();
        this.loader = null;
      }
    },
    async confirmReactivateSubscription(plan) {
      try {
        const result = await this.$swal({
          title: this.$t("message.areYouSure"),
          html: this.$t("message.reactivateSubscriptionConfirmationHTML")
            .replace("{{plan}}", plan._id)
            .replace("{{price}}", "€" + plan.price),
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: this.$t("message.goBack"),
          confirmButtonText: this.$t("message.confirm")
        });

        if (!result.value) return;

        this.loader = this.$loading.show();

        await this.changeSubscription(plan);
        this.$router.push("/account/overview");
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.reactivateSubscriptionSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.reactivateSubscriptionFailure")
        });
      } finally {
        this.loader.hide();
        this.loader = null;
      }
    },
    featureClassToIcon(_class) {
      if (_class === "included") return "ui-1_check";
      if (_class === "star") return "objects_diamond";
      return "ui-1_simple-remove";
    }
  },
  computed: {
    ...mapGetters([
      "subscription",
      "plansList",
      "subscriptionIsCancelled",
      "subscriptionIsTrialPeriod",
      "subscriptionIsExpired",
      "userId",
      "userClaims",
      "isSaasBilledTenant"
    ]),
    ...mapGetters({ shop: "shops/shop" }),
    saasShop() {
      return this.isSaasBilledTenant;
    },
    saasPlans() {
      return [
        {
          ...SAAS_PLAN,
          _id: SAAS_PLAN.id,
          features: [
            {
              title: "The book",
              description: "Diary, staff, services, and invoices for one shop.",
              class: "included"
            }
          ]
        }
      ];
    }
  },
  created() {
    this.fetchPlans();
  }
};
</script>
<style lang="scss" scoped></style>
